import { useEffect, useState, useCallback } from 'react';
import { Activity, Shield, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { AuditEventItem } from '../types';

export function ActivityPage() {
  const { activeOrg } = useAuth();
  const [events, setEvents] = useState<AuditEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    if (!activeOrg?.id) return;
    try {
      const data = await api.orgs.listActivity(activeOrg.id);
      setEvents(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeOrg?.id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadEvents();
  };

  if (loading) {
    return <Loader label="Loading audit logs..." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Audit & Activity Trail</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
            Immutable security and administrative log for <strong style={{ color: 'var(--text-primary)' }}>{activeOrg?.display_name}</strong>.
          </p>
        </div>

        <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing} icon={<RefreshCw size={14} className={refreshing ? 'spin' : ''} />}>
          {refreshing ? 'Refreshing...' : 'Refresh Activity'}
        </Button>
      </div>

      {error && (
        <Alert tone="danger" title="Error">
          {error}
        </Alert>
      )}

      <Card padding="0">
        {events.length === 0 ? (
          <div style={{ padding: 'var(--space-12)', textAlign: 'center', color: 'var(--text-tertiary)' }}>
            <Activity size={36} style={{ margin: '0 auto 12px' }} />
            <div style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: 'var(--text-primary)' }}>
              No audit events recorded
            </div>
            <div style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
              Activity will automatically register as changes occur in this organisation.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'var(--text-sm)' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-inset)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Timestamp</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Action</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Actor</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Target</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textTransform: 'uppercase' }}>Details / Metadata</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt, idx) => (
                  <tr key={evt.id || idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '12px 16px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                      {new Date(evt.created_at).toLocaleString()}
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--navy-700)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--accent)',
                        }}
                      >
                        {evt.action}
                      </span>
                    </td>

                    <td style={{ padding: '12px 16px' }}>
                      <Badge tone={evt.actor_type === 'user' ? 'accent' : 'neutral'}>
                        {evt.actor_type.toUpperCase()}
                      </Badge>
                    </td>

                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                      {evt.target_type ? (
                        <span>{evt.target_type} {evt.target_id ? `(${evt.target_id.slice(0, 8)}...)` : ''}</span>
                      ) : (
                        <span>—</span>
                      )}
                    </td>

                    <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                      {evt.event_metadata && Object.keys(evt.event_metadata).length > 0 ? (
                        <code>{JSON.stringify(evt.event_metadata)}</code>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
        <Shield size={14} color="var(--accent)" />
        <span>SPEC-05 • Append-only audit storage keyed by Organisation</span>
      </div>
    </div>
  );
}
